import React, { useState } from 'react';
import api from '../../utils/api';

export default function CreateSublease({ onClose, existing }) {
  const [form, setForm] = useState({
    address: existing?.address || '',
    city: existing?.city || 'Iowa City',
    state: existing?.state || 'IA',
    zip: existing?.zip || '',
    monthly_rent: existing?.monthly_rent || '',
    sublease_start: existing?.sublease_start?.split('T')[0] || '',
    sublease_end: existing?.sublease_end?.split('T')[0] || '',
    bedrooms: existing?.bedrooms || '',
    bathrooms: existing?.bathrooms || '',
    parking: existing?.parking || '',
    description: existing?.description || '',
    reason_for_subleasing: existing?.reason_for_subleasing || '',
  });
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => { if (v !== '') data.append(k, v); });
      photos.forEach(f => data.append('photos', f));

      if (existing) {
        await api.put(`/subleases/${existing.id}`, data);
      } else {
        await api.post('/subleases', data);
      }
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save sublease');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button onClick={onClose} className="btn-ghost border border-[#DDDDDD]">← Back</button>
        <h1 className="section-title">{existing ? 'Edit Sublease' : 'Post a Sublease'}</h1>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5 text-red-600 text-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        <div>
          <label className="label">Street Address</label>
          <input className="input" placeholder="123 Burlington St, Iowa City" value={form.address}
            onChange={e => setForm({ ...form, address: e.target.value })} required />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">City</label>
            <input className="input" value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} required />
          </div>
          <div>
            <label className="label">ZIP</label>
            <input className="input" placeholder="52240" value={form.zip} onChange={e => setForm({ ...form, zip: e.target.value })} />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Monthly Rent</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#AAAAAA]">$</span>
              <input type="number" className="input pl-7" placeholder="700" value={form.monthly_rent}
                onChange={e => setForm({ ...form, monthly_rent: e.target.value })} required />
            </div>
          </div>
          <div>
            <label className="label">Bedrooms</label>
            <input type="number" className="input" placeholder="1" min="1" value={form.bedrooms}
              onChange={e => setForm({ ...form, bedrooms: e.target.value })} required />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Bathrooms</label>
            <input type="number" className="input" placeholder="1" step="0.5" min="1" value={form.bathrooms}
              onChange={e => setForm({ ...form, bathrooms: e.target.value })} />
          </div>
          <div>
            <label className="label">Parking</label>
            <select className="input" value={form.parking} onChange={e => setForm({ ...form, parking: e.target.value })}>
              <option value="">Select...</option>
              <option value="reserved">Reserved Parking Space</option>
              <option value="driveway">Driveway</option>
              <option value="street">Street Parking</option>
              <option value="paid">Parking Costs Extra</option>
              <option value="none">No Parking Available</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="label">Sublease Start</label>
            <input type="date" className="input" value={form.sublease_start}
              onChange={e => setForm({ ...form, sublease_start: e.target.value })} required />
          </div>
          <div>
            <label className="label">Sublease End</label>
            <input type="date" className="input" value={form.sublease_end}
              onChange={e => setForm({ ...form, sublease_end: e.target.value })} required />
          </div>
        </div>

        <div>
          <label className="label">Description</label>
          <textarea className="input h-24 resize-none" placeholder="Describe the apartment..." value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })} />
        </div>

        <div>
          <label className="label">
            Reason for Subleasing{' '}
            <span className="text-[#AAAAAA] normal-case font-normal">(optional)</span>
          </label>
          <input className="input" placeholder="Studying abroad, internship..." value={form.reason_for_subleasing}
            onChange={e => setForm({ ...form, reason_for_subleasing: e.target.value })} />
        </div>

        <div>
          <label className="label">Photos</label>
          <input type="file" accept="image/*" multiple className="input" onChange={e => setPhotos(Array.from(e.target.files))} />
          {photos.length > 0 && <p className="nav-link text-sm text-[#777777] mt-1">{photos.length} photo(s) selected</p>}
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn-ghost flex-1 border border-[#DDDDDD]">Cancel</button>
          <button type="submit" disabled={loading} className="btn-primary flex-1">
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-[#C8A800] border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            ) : existing ? 'Save Changes' : 'Post Sublease'}
          </button>
        </div>
      </form>
    </div>
  );
}
